import { useEffect, useState } from "react";
import {
    adminStuckUsersList,
    adminStuckUsersDryRun,
    adminStuckUsersRunDrip,
    adminStuckUsersResend,
} from "@/lib/api";
import { toast } from "sonner";
import { UserMinus, Mail, RefreshCw } from "lucide-react";

const STAGES = [
    { key: "day1", label: "Day 1" },
    { key: "day3", label: "Day 3" },
    { key: "day7", label: "Day 7" },
];

// "Stuck users" admin tile — surfaces every non-admin, non-test user who
// has signed up but never uploaded. Each row gets per-stage resend
// buttons so the operator can manually re-poke a user with the right
// lifecycle email. A "Run drip now" button forces the cron's full sweep
// on demand, useful when STUCK_USER_DRIP_ENABLED is OFF and the admin
// wants to send a one-time wave.
export default function StuckUsersPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dryRun, setDryRun] = useState(null);
    const [running, setRunning] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminStuckUsersList(200);
            setData(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load stuck users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const runDryRun = async () => {
        setRunning(true);
        try {
            const r = await adminStuckUsersDryRun();
            setDryRun(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Dry-run failed");
        } finally {
            setRunning(false);
        }
    };

    const runDripNow = async () => {
        if (!confirm("Send the full Day 1 / Day 3 / Day 7 drip wave right now to all eligible users?")) return;
        setRunning(true);
        try {
            const r = await adminStuckUsersRunDrip();
            const sent = r?.total_sent || 0;
            const candidates = r?.total_candidates || 0;
            toast.success(`Drip wave sent · ${sent}/${candidates} emails delivered`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Drip run failed");
        } finally {
            setRunning(false);
        }
    };

    const resend = async (user_id, stage) => {
        try {
            const r = await adminStuckUsersResend({ user_id, stage });
            if (r?.ok) toast.success(`${stage} email resent`);
            else toast.warning(`${stage} email did not send (throttled or sandbox mode)`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Resend failed");
        }
    };

    const rows = data?.stuck_users || [];

    return (
        <div
            className="bl-card p-5 mt-6"
            data-testid="admin-stuck-users-panel"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    <UserMinus className="w-5 h-5 text-[var(--bl-teal)] mt-0.5 shrink-0" aria-hidden />
                    <div className="min-w-0">
                        <div className="font-display font-semibold text-bl text-lg">
                            Stuck users — signed up, never uploaded
                        </div>
                        <div className="mt-0.5 text-xs text-bl-muted">
                            {rows.length} user{rows.length === 1 ? "" : "s"} with zero uploads.{" "}
                            Drip cron is{" "}
                            <span className={data?.drip_enabled ? "text-[var(--bl-moss)] font-semibold" : "text-[var(--bl-amber)] font-semibold"}>
                                {data?.drip_enabled ? "ON" : "OFF"}
                            </span>
                            {!data?.drip_enabled && (
                                <> · set <code>STUCK_USER_DRIP_ENABLED=true</code> to auto-fire</>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                    <button
                        type="button"
                        onClick={runDryRun}
                        disabled={running}
                        className="bl-btn-ghost text-xs"
                        data-testid="admin-stuck-users-dryrun-btn"
                    >
                        Dry-run
                    </button>
                    <button
                        type="button"
                        onClick={runDripNow}
                        disabled={running}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[var(--bl-teal)] text-white hover:opacity-90 transition-opacity"
                        data-testid="admin-stuck-users-run-btn"
                    >
                        {running ? "Running…" : "Run drip now"}
                    </button>
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="bl-btn-ghost text-xs"
                        aria-label="Refresh"
                        data-testid="admin-stuck-users-refresh-btn"
                    >
                        <RefreshCw className={"w-3.5 h-3.5 " + (loading ? "animate-spin" : "")} />
                    </button>
                </div>
            </div>

            {dryRun && (
                <div
                    className="mt-3 text-[11px] bg-[var(--bl-teal-tint)] border border-[var(--bl-teal)]/30 rounded p-2"
                    data-testid="admin-stuck-users-dryrun"
                >
                    <div className="font-display font-semibold text-bl">
                        Next cron tick would send {dryRun.total_candidates || 0} email{dryRun.total_candidates === 1 ? "" : "s"}
                    </div>
                    <div className="mt-1 text-bl-muted flex flex-wrap gap-x-3 gap-y-0.5">
                        {(dryRun.stages || []).map((s) => (
                            <span key={s.stage}>
                                <b>{s.stage}</b>: {s.candidates}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {loading && !data && (
                <div className="mt-4 text-center text-sm text-bl-muted py-6">
                    Loading…
                </div>
            )}

            {rows.length === 0 && data && (
                <div
                    className="mt-4 text-sm text-bl-muted py-6 text-center bg-bl-bg border border-bl rounded-md"
                    data-testid="admin-stuck-users-empty"
                >
                    🎉 Zero stuck users — every signed-up customer has uploaded at least one inspection.
                </div>
            )}

            {rows.length > 0 && (
                <div className="mt-4 overflow-x-auto" data-testid="admin-stuck-users-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                                <th className="text-left py-2 px-2 font-medium">Email</th>
                                <th className="text-left py-2 px-2 font-medium">Name</th>
                                <th className="text-left py-2 px-2 font-medium">Days</th>
                                <th className="text-left py-2 px-2 font-medium">Opt-in</th>
                                <th className="text-right py-2 px-2 font-medium">Resend</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => (
                                <tr
                                    key={u.user_id}
                                    className="border-t border-bl-line"
                                    data-testid={`admin-stuck-user-${u.user_id}`}
                                >
                                    <td className="py-2 px-2 font-mono-bl text-bl truncate max-w-[18rem]">
                                        {u.email}
                                    </td>
                                    <td className="py-2 px-2 text-bl-muted truncate max-w-[10rem]">
                                        {u.name || "—"}
                                    </td>
                                    <td className="py-2 px-2 text-bl tnum">
                                        {u.days_since_signup != null ? `${u.days_since_signup}d` : "—"}
                                    </td>
                                    <td className="py-2 px-2">
                                        <span className={u.email_opt_in
                                            ? "text-[var(--bl-moss)] text-xs font-semibold"
                                            : "text-bl-muted text-xs"}>
                                            {u.email_opt_in ? "yes" : "no"}
                                        </span>
                                    </td>
                                    <td className="py-2 px-2 text-right">
                                        <div className="inline-flex gap-1">
                                            {STAGES.map((s) => (
                                                <button
                                                    key={s.key}
                                                    type="button"
                                                    onClick={() => resend(u.user_id, s.key)}
                                                    disabled={!u.email_opt_in}
                                                    title={`Resend ${s.label} email`}
                                                    className="px-2 py-0.5 rounded text-[10px] font-semibold border border-bl bg-bl-surface hover:bg-[var(--bl-teal-tint)] hover:border-[var(--bl-teal)]/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                    data-testid={`admin-stuck-resend-${u.user_id}-${s.key}`}
                                                >
                                                    <Mail className="inline w-3 h-3 -mt-0.5 mr-0.5" />
                                                    {s.label}
                                                </button>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
